import { useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import { create } from 'zustand'
import { api } from '@/lib/api'
import {
  useAuthStore,
  type DensityPreference,
  type EmployeePreferences,
  type SessionEmployee,
  type ThemePreference,
} from '@/lib/auth'
import { updateThemeColorMeta } from '@/lib/branding'
import { STORAGE_KEYS } from '@/lib/constants'

/** Consulta de medios para el tema oscuro del sistema operativo. */
const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)'
/** Atributo en `<html>` que activa los estilos de densidad compacta. */
const DENSITY_ATTRIBUTE = 'data-density'

const THEMES: readonly ThemePreference[] = ['light', 'dark', 'system']
const DENSITIES: readonly DensityPreference[] = ['comfortable', 'compact']

/** Etiquetas en español de las opciones de tema. */
export const THEME_LABELS: Record<ThemePreference, string> = {
  light: 'Claro',
  dark: 'Oscuro',
  system: 'Según el dispositivo',
}

/** Etiquetas en español de las opciones de densidad. */
export const DENSITY_LABELS: Record<DensityPreference, string> = {
  comfortable: 'Cómoda (táctil)',
  compact: 'Compacta',
}

function readStored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const value = localStorage.getItem(key)
    return value && (allowed as readonly string[]).includes(value) ? (value as T) : fallback
  } catch {
    return fallback
  }
}

function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* almacenamiento bloqueado */
  }
}

/** ¿El tema efectivo es oscuro? (resuelve "system" con la preferencia del SO). */
export function resolveDark(theme: ThemePreference): boolean {
  if (theme === 'system') return typeof matchMedia !== 'undefined' && matchMedia(DARK_SCHEME_QUERY).matches
  return theme === 'dark'
}

function applyTheme(theme: ThemePreference): void {
  document.documentElement.classList.toggle('dark', resolveDark(theme))
  updateThemeColorMeta()
}

function applyDensity(density: DensityPreference): void {
  document.documentElement.setAttribute(DENSITY_ATTRIBUTE, density)
}

interface PreferencesState {
  theme: ThemePreference
  density: DensityPreference
  setTheme: (theme: ThemePreference) => void
  setDensity: (density: DensityPreference) => void
  /** Alterna claro/oscuro (atajo ⇧T). */
  toggleTheme: () => void
  /** Adopta las preferencias guardadas en el servidor tras iniciar sesión. */
  adopt: (preferences: EmployeePreferences) => void
}

/** Preferencias visuales locales (tema y densidad), aplicadas al documento. */
export const usePreferencesStore = create<PreferencesState>((set, get) => ({
  theme: readStored(STORAGE_KEYS.theme, THEMES, 'system'),
  density: readStored(STORAGE_KEYS.density, DENSITIES, 'comfortable'),
  setTheme(theme) {
    writeStored(STORAGE_KEYS.theme, theme)
    applyTheme(theme)
    set({ theme })
  },
  setDensity(density) {
    writeStored(STORAGE_KEYS.density, density)
    applyDensity(density)
    set({ density })
  },
  toggleTheme() {
    get().setTheme(resolveDark(get().theme) ? 'light' : 'dark')
  },
  adopt(preferences) {
    if (preferences.theme && preferences.theme !== get().theme) get().setTheme(preferences.theme)
    if (preferences.density && preferences.density !== get().density) get().setDensity(preferences.density)
  },
}))

/**
 * Aplica tema y densidad al montar y sigue los cambios del sistema operativo
 * cuando el tema es "system". Montar una vez en la raíz.
 */
export function usePreferencesBootstrap(): void {
  const theme = usePreferencesStore((s) => s.theme)
  const density = usePreferencesStore((s) => s.density)
  useEffect(() => {
    applyTheme(theme)
    if (theme !== 'system' || typeof matchMedia === 'undefined') return
    const media = matchMedia(DARK_SCHEME_QUERY)
    const onChange = () => applyTheme('system')
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [theme])
  useEffect(() => applyDensity(density), [density])
}

/**
 * Mutación que guarda preferencias en el servidor (`PATCH /employees/me`)
 * y actualiza el usuario en memoria. Aplica tema/densidad de inmediato.
 */
export function useSavePreferences() {
  return useMutation({
    mutationFn: (preferences: EmployeePreferences) => api.patch<SessionEmployee>('/employees/me', { preferences }),
    onMutate: (preferences) => usePreferencesStore.getState().adopt(preferences),
    onSuccess: (employee) => useAuthStore.getState().setEmployee(employee),
  })
}

/**
 * Alterna claro/oscuro (atajo ⇧T, menú de usuario, paleta) y guarda la
 * elección en el perfil sin bloquear la interfaz; si falla, queda local.
 */
export function toggleThemeAndPersist(): void {
  const store = usePreferencesStore.getState()
  store.toggleTheme()
  const theme = usePreferencesStore.getState().theme
  if (!useAuthStore.getState().accessToken) return
  api
    .patch<SessionEmployee>('/employees/me', { preferences: { theme } })
    .then((employee) => useAuthStore.getState().setEmployee(employee))
    .catch(() => undefined)
}
